import { payPeriodApi } from 'Api/payPeriod';
import { timeActivitiesApi } from 'Api/timeActivities';
import { timeLogsAssignmentApi } from 'Api/timeLogsAssignment';
import { Button, Space } from 'antd';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { TablePagination, TableSorter } from 'interfaces/global.interface';
import { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch } from 'redux/store';
import { invalidText, toastText } from 'utils/utils';
import { fetchConstantDropdownAction } from '../../redux/actions/constantDropdownAction';
import {
	initializeWithCurrentPayPeriod,
	resetFilters,
	setFilters,
	setInitialized,
	setPayPeriods,
	setPayPeriodsLoading,
	setTimeActivities,
	setTimeActivitiesLoading,
	syncPayPeriodIndex,
	updatePagination,
	updateSorting,
} from '../../redux/slices/monthlySummary';
import TimeActivityHeader from './Header';
import MonthlyTimeLogsModal from './MonthlyAddLogsModal';
import TimeActivitiesTable from './TimeActivitiesTable';
import styles from './index.module.scss';
import useDebounce from 'components/Global/Hooks/UseDebounce';
import { PermissionContext } from 'components/Global/AuthLayout';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const TimeActivitiesComponent = () => {
	const dispatch = useDispatch<AppDispatch>();

	// Redux selectors
	const { filters, data, loading, initialized } = useSelector(
		(state: any) => state.monthlySummary,
	);

	const dropdownOptions = useSelector(
		(state: any) => state.constantDropdown.data,
	);

	const { isLoading: dropdownOptionLoading } = useSelector(
		(state: any) => state.constantDropdown,
	);
	const [payrollViewEnabled, setPayrollViewEnabled] = useState(false);
	const [isAddLogsModalOpen, setIsAddLogsModalOpen] = useState(false);
	const [logSource, setLogSource] = useState<string>('');

	// Destructure for easier access
	const {
		currentPage,
		pageSize,
		sortField,
		sortOrder,
		date,
		statusFilterValue,
		currentPayPeriodIndex,
		staffCode,
	} = filters;

	const { timeActivities, totalRecords, payPeriods } = data;
	const {
		timeActivities: timeActivitiesLoading,
		payPeriods: payPeriodsLoading,
	} = loading;
	const navigate = useNavigate();

	const [searchText, setSearchText] = useState('');
	const debouncedSearchValue = useDebounce(searchText, 500);

	const permissionContext = useContext(PermissionContext);

	const isAdd = permissionContext.allowedPermissions.includes('Add_Time_Logs');
	const isEdit =
		permissionContext.allowedPermissions.includes('Edit_Time_Logs');

	// Helper functions to update filters
	const updateFilters = (newFilters: Partial<typeof filters>) => {
		dispatch(setFilters(newFilters));
	};

	const handleStatusFilter = (value: string) => {
		updateFilters({
			statusFilterValue: value,
			currentPage: 1,
		});
	};

	const handleDatePicker = (value: [string, string]) => {
		updateFilters({
			date: value,
			currentPage: 1,
		});
	};
	const handleSearch = (value: string) => {
		setSearchText(value);
		updateFilters({
			currentPage: 1,
		});
	};

	const handleStaffCodeChange = (newStaffCode: string) => {
		if (newStaffCode === 'MONTHLY') {
			setPayrollViewEnabled(false);
		}

		setSearchText('');
		updateFilters({
			staffCode: newStaffCode,
			currentPage: 1,
		});
	};

	const handlePayPeriodChange = (index: number, payPeriod: any) => {
		updateFilters({
			currentPayPeriodIndex: index,
			date: [payPeriod.startDate, payPeriod.endDate],
			currentPage: 1,
		});
	};

	const handlePayrollViewToggle = (enabled: boolean) => {
		setPayrollViewEnabled(enabled);
	};
	const payPeriodsForNavigation = [...payPeriods].sort(
		(a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
	);

	const tableChangeHandler = (
		pagination: TablePagination,
		filters: any,
		sorter: TableSorter,
	) => {
		// Update pagination
		dispatch(
			updatePagination({
				currentPage: pagination.current,
				pageSize: pagination.pageSize,
				totalRecords: pagination.total,
			}),
		);

		// Update sorting
		const newSortOrder =
			sorter?.order === 'ascend' ? 'asc' : sorter?.order ? 'desc' : '';
		dispatch(
			updateSorting({
				sortField: sorter.field,
				sortOrder: newSortOrder,
			}),
		);
	};

	// Reset filters function
	const clearFilters = () => {
		dispatch(resetFilters());
		setPayrollViewEnabled(false);
	};

	const payPeriodDateOptions = async () => {
		dispatch(setPayPeriodsLoading(true));
		try {
			const response = await payPeriodApi.getPayPeriodDateOptions();
			const responseData = response.data.data || [];

			dispatch(setPayPeriods(responseData));

			if (responseData.length > 0) {
				if (initialized) {
					// If already initialized (returning from another screen), sync the index
					dispatch(
						syncPayPeriodIndex({
							payPeriods: responseData,
							currentDate: date,
						}),
					);
				} else {
					// First time initialization, set current pay period
					dispatch(initializeWithCurrentPayPeriod(responseData));
				}
			}

			dispatch(setInitialized(true));
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message ||
				'Something went wrong in fetching Pay Periods';
			toastText(errorMessage, 'error');
			dispatch(setInitialized(true));
		}
		dispatch(setPayPeriodsLoading(false));
	};

	const fetchEmployeesTimeActivities = async () => {
		dispatch(setTimeActivitiesLoading(true));
		try {
			let employeesTimeActivity;

			if (staffCode === 'HOURLY') {
				const query = {
					page: currentPage,
					pageSize: pageSize,
					sortBy: sortField,
					sortOrder: sortOrder,
					supervisorId: statusFilterValue,
					startDate: dayjs(date[0]).format('YYYY-MM-DD'),
					endDate: dayjs(date[1]).format('YYYY-MM-DD'),
					search: searchText,
				};
				employeesTimeActivity =
					await timeLogsAssignmentApi.getTimeActivity(query);
			} else {
				const query = {
					page: currentPage,
					pageSize: pageSize,
					sortBy: sortField,
					sortOrder: sortOrder,
					supervisorId: statusFilterValue,
					startDate: dayjs(date[0]).format('YYYY-MM-DD'),
					endDate: dayjs(date[1]).format('YYYY-MM-DD'),
					search: searchText,
				};
				employeesTimeActivity =
					await timeActivitiesApi.getEmployeeTimeActivitiesByStaffCode(query);
			}

			setLogSource(employeesTimeActivity.data.source);
			dispatch(
				setTimeActivities({
					timeActivities: employeesTimeActivity.data.timeActivities,
					totalRecords: employeesTimeActivity.data.totalRecords,
				}),
			);
		} catch (err: any) {
			const errorMessage =
				err?.response?.data?.message ||
				'Something went wrong in fetching employees';
			toastText(errorMessage, 'error');

			dispatch(
				setTimeActivities({
					timeActivities: [],
					totalRecords: 0,
				}),
			);
		}
		dispatch(setTimeActivitiesLoading(false));
	};

	// Fetch dropdowns once
	useEffect(() => {
		dispatch(fetchConstantDropdownAction({}));
	}, [dispatch]);

	// Fetch pay periods on mount
	useEffect(() => {
		payPeriodDateOptions();
	}, []);

	// Fetch time activities when filters change and component is initialized
	useEffect(() => {
		if (initialized) {
			fetchEmployeesTimeActivities();
		}
	}, [
		currentPage,
		statusFilterValue,
		sortField,
		sortOrder,
		pageSize,
		date,
		staffCode,
		initialized,
		debouncedSearchValue,
	]);

	const handleClick = () => {
		const url = `/time-logs?isDateDisable=false&date=${dayjs(date[0]).format(
			'YYYY-MM-DD',
		)}${
			invalidText(statusFilterValue) ? '' : `&supervisor=${statusFilterValue}`
		}`;
		navigate(url, {
			state: { from: '/daily-attendance' },
		});
	};

	return (
		<>
			<div
				style={{
					maxHeight: 'calc(100vh - 110px)',
				}}
			>
				<div className={styles['monthly-summary-header']}>
					<p>Monthly Summary</p>
					{/* Optional: Add reset filters button */}
					<Space>
						{staffCode === 'MONTHLY' && (isAdd || isEdit) ? (
							<Space>
								{/* <Tooltip title="Add Monthly Time Logs"> */}
								<Button
									type="primary"
									onClick={() => {
										setIsAddLogsModalOpen(true);
									}}
								>
									Add Monthly Logs
								</Button>
								{/* </Tooltip> */}
							</Space>
						) : staffCode === 'HOURLY' && (isAdd || isEdit) ? (
							<Space>
								<Button type="primary" onClick={handleClick}>
									Add Time Logs
								</Button>
							</Space>
						) : null}
					</Space>
				</div>
			</div>
			<div className={styles['employee-container']}>
				<TimeActivityHeader
					statusFilterValue={statusFilterValue}
					handleStatusFilter={handleStatusFilter}
					options={dropdownOptions?.supervisors}
					searchText={searchText}
					handleSearch={handleSearch}
					dropdownOptionLoading={dropdownOptionLoading}
					handleStaffCodeChange={handleStaffCodeChange}
					staffCode={staffCode}
					payPeriodOptions={payPeriods}
					payPeriodLoading={payPeriodsLoading}
					currentPayPeriodIndex={currentPayPeriodIndex}
					onPayPeriodChange={handlePayPeriodChange}
					handleDatePicker={handleDatePicker}
					dateValue={date}
					payrollViewEnabled={payrollViewEnabled}
					onPayrollViewToggle={handlePayrollViewToggle}
					payPeriodsForNavigation={payPeriodsForNavigation}
					fetchEmployeesTimeActivities={fetchEmployeesTimeActivities}
					logSource={logSource}
				/>
				<TimeActivitiesTable
					tableData={timeActivities}
					totalRecords={totalRecords}
					pageSize={pageSize}
					currentPage={currentPage}
					tableChangeHandler={tableChangeHandler}
					isLoading={timeActivitiesLoading}
					dateRange={date}
					staffCode={staffCode}
					fetchEmployeesTimeActivities={fetchEmployeesTimeActivities}
					payrollViewEnabled={payrollViewEnabled}
				/>
			</div>
			{isAddLogsModalOpen && (
				<MonthlyTimeLogsModal
					isOpen={isAddLogsModalOpen}
					handleCancel={() => {
						setIsAddLogsModalOpen(false);
					}}
					startDate={date[0]}
					fetchEmployeesTimeActivities={fetchEmployeesTimeActivities}
				/>
			)}
		</>
	);
};

export default TimeActivitiesComponent;
